import { Fragment, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from '../icons';
import { Nova } from '../Nova';
import { formatMoney, moneySign } from '../../../lib/format';

// Small display primitives: Badge/Chip, Avatar, Skeleton, EmptyState, Kbd/KeyboardHint,
// Separator, ScrollArea, Progress, Stat, Breadcrumb, PageHeader, LifecycleRail, Banner,
// Loading, Money.

export type Tone = 'pos' | 'neg' | 'warn' | 'info' | 'accent' | 'muted';

export function Badge({ tone = 'muted', children, dot = true, outline, className, title }: { tone?: Tone; children: ReactNode; dot?: boolean; outline?: boolean; className?: string; title?: string }) {
  return (
    <span className={['hf-badge', tone, !dot && 'nodot', outline && 'outline', className].filter(Boolean).join(' ')} title={title}>
      {children}
    </span>
  );
}

/** Domain statuses → tone. Extend via `tones`; unknown statuses fall back to muted. */
export const STATUS_TONES: Record<string, Tone> = {
  draft: 'muted', issued: 'info', frozen: 'warn', voided: 'neg', paid: 'pos', overdue: 'neg',
  pending: 'warn', active: 'pos', expired: 'muted', cancelled: 'neg', inactive: 'muted', prospect: 'info',
  open: 'info', submitted: 'warn', approved: 'accent', locked: 'pos',
  unresolved: 'neg', resolved: 'pos', investigating: 'warn', ignored: 'muted',
  new: 'info', closed: 'muted', done: 'pos', in_progress: 'accent', blocked: 'neg',
  credit_note: 'info', invoice: 'muted', employee: 'accent', contractor: 'info',
};

export function StatusBadge({ status, tones }: { status: string | null | undefined; tones?: Record<string, Tone> }) {
  const s = (status ?? '').toLowerCase();
  const tone = tones?.[s] ?? STATUS_TONES[s] ?? 'muted';
  return <Badge tone={tone}>{status ? status.replace(/_/g, ' ') : '—'}</Badge>;
}

export function Chip({
  active,
  count,
  onRemove,
  children,
  icon,
  onClick,
  className,
  role,
  'aria-selected': ariaSelected,
  disabled,
}: {
  active?: boolean;
  count?: number | string;
  onRemove?: () => void;
  children: ReactNode;
  icon?: IconName;
  onClick?: () => void;
  className?: string;
  role?: string;
  'aria-selected'?: boolean;
  disabled?: boolean;
}) {
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      role={role}
      aria-selected={ariaSelected}
      aria-pressed={onClick && role !== 'tab' ? active : undefined}
      className={['hf-chip', active && 'active', !onClick && 'static', className].filter(Boolean).join(' ')}
      onClick={onClick}
      disabled={disabled}
    >
      {icon && <Icon name={icon} size={12} />}
      {children}
      {count != null && <span className="count">{count}</span>}
      {onRemove && (
        <span
          className="x"
          role="button"
          aria-label="Remove"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }
          }}
        >
          <Icon name="x" size={11} />
        </span>
      )}
    </Tag>
  );
}

export function Avatar({ name, src, size = 'md', title }: { name?: string | null; src?: string | null; size?: 'sm' | 'md' | 'lg'; title?: string }) {
  const initials = (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
  return (
    <span className={['hf-avatar', size !== 'md' && size].filter(Boolean).join(' ')} title={title ?? name ?? undefined} aria-label={name ?? undefined} role={name ? 'img' : undefined}>
      {src ? <img src={src} alt="" /> : initials || '?'}
    </span>
  );
}

export function Skeleton({ width, height = 14, style, lines = 1, className }: { width?: number | string; height?: number | string; style?: CSSProperties; lines?: number; className?: string }) {
  if (lines > 1) {
    return (
      <div className="hf-stack" style={{ gap: 8 }} aria-hidden>
        {Array.from({ length: lines }, (_, i) => (
          <span key={i} className="hf-skeleton" style={{ width: i === lines - 1 ? '60%' : width ?? '100%', height, ...style }} />
        ))}
      </div>
    );
  }
  return <span className={['hf-skeleton', className].filter(Boolean).join(' ')} style={{ width: width ?? '100%', height, ...style }} aria-hidden />;
}

export function EmptyState({ title, hint, icon = 'inbox', action, children }: { title: string; hint?: ReactNode; icon?: IconName; action?: ReactNode; children?: ReactNode }) {
  return (
    <div className="hf-empty" role="status">
      <div className="glyph">
        <Icon name={icon} size={20} weight={1.5} />
      </div>
      <h3>{title}</h3>
      {hint && <p>{hint}</p>}
      {children}
      {action && <div className="act">{action}</div>}
    </div>
  );
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
/** "mod+k" → ⌘K / Ctrl+K etc. */
export function keyLabel(key: string): string {
  return key
    .split('+')
    .map((k) => {
      const l = k.toLowerCase();
      if (l === 'mod') return IS_MAC ? '⌘' : 'Ctrl';
      if (l === 'shift') return IS_MAC ? '⇧' : 'Shift';
      if (l === 'alt') return IS_MAC ? '⌥' : 'Alt';
      if (l === 'enter') return '↵';
      if (l === 'esc' || l === 'escape') return 'Esc';
      if (l === 'up') return '↑';
      if (l === 'down') return '↓';
      if (l === 'left') return '←';
      if (l === 'right') return '→';
      return k.length === 1 ? k.toUpperCase() : k;
    })
    .join(IS_MAC ? '' : '+');
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="hf-kbd">{children}</kbd>;
}

export function KeyboardHint({ keys, label }: { keys: string | string[]; label?: ReactNode }) {
  const list = Array.isArray(keys) ? keys : [keys];
  return (
    <span className="hf-kbdhint">
      {list.map((k, i) => (
        <Fragment key={i}>
          <Kbd>{keyLabel(k)}</Kbd>
          {i < list.length - 1 && <span>then</span>}
        </Fragment>
      ))}
      {label && <span>{label}</span>}
    </span>
  );
}

export function Separator({ vertical, className, style }: { vertical?: boolean; className?: string; style?: CSSProperties }) {
  return <hr className={['hf-rule', vertical && 'vertical', className].filter(Boolean).join(' ')} style={style} aria-orientation={vertical ? 'vertical' : undefined} />;
}

export function ScrollArea({ children, className, style, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={['hf-scroll', className].filter(Boolean).join(' ')} style={style} {...rest}>
      {children}
    </div>
  );
}

export function Progress({ value, tone, title, indeterminate, className }: { value?: number; tone?: 'warn' | 'neg' | 'pos'; title?: string; indeterminate?: boolean; className?: string }) {
  const w = value != null && Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
  return (
    <span
      className={['hf-progress', tone, indeterminate && 'indeterminate', className].filter(Boolean).join(' ')}
      title={title}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={indeterminate ? undefined : Math.round(w * 100)}
      aria-label={title}
    >
      <i style={{ ['--w' as string]: String(w) }} />
    </span>
  );
}

/**
 * A stat tile: caps label, a big tabular number, a sub-line — plus, optionally, a micro-visual
 * (primitives/Viz), a ▴/▾ delta, up to a couple of preview rows and a footer call to action
 * ("Chase AR ›"). The surrounding Tile decides whether it links or opens a drill-down.
 */
export function Stat({ label, value, sub, accent, icon, visual, delta, preview, foot }: { label: ReactNode; value: ReactNode; sub?: ReactNode; accent?: boolean; icon?: IconName; visual?: ReactNode; delta?: ReactNode; preview?: ReactNode[]; foot?: ReactNode }) {
  return (
    <div className={['hf-stat', accent && 'accent', visual && 'has-visual'].filter(Boolean).join(' ')}>
      <div className="label">
        {icon && <Icon name={icon} size={12} />}
        {label}
      </div>
      <div className="value">{value}</div>
      {sub != null && <div className="sub">{sub}</div>}
      {delta != null && <div className="sub">{delta}</div>}
      {visual != null && <div className="visual">{visual}</div>}
      {preview && preview.length > 0 && <div className="preview">{preview.map((p, i) => <span key={i}>{p}</span>)}</div>}
      {foot != null && <div className="foot">{foot}</div>}
    </div>
  );
}

export interface Crumb {
  label: ReactNode;
  to?: string;
}

export function Breadcrumb({ items }: { items: Crumb[] }) {
  return (
    <nav className="hf-breadcrumb" aria-label="Breadcrumb">
      {items.map((c, i) => (
        <Fragment key={i}>
          {i > 0 && (
            <span className="sep" aria-hidden>
              /
            </span>
          )}
          {c.to && i < items.length - 1 ? (
            <Link to={c.to}>{c.label}</Link>
          ) : (
            <span className={i === items.length - 1 ? 'cur' : undefined} aria-current={i === items.length - 1 ? 'page' : undefined}>
              {c.label}
            </span>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

export function PageHeader({
  title,
  eyebrow,
  subtitle,
  actions,
  breadcrumb,
  badge,
  compact,
  children,
}: {
  title: ReactNode;
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: Crumb[];
  badge?: ReactNode;
  compact?: boolean;
  children?: ReactNode;
}) {
  return (
    <header className={['hf-pagehead', compact && 'compact'].filter(Boolean).join(' ')}>
      <div className="titles">
        {breadcrumb && <Breadcrumb items={breadcrumb} />}
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>
          {title}
          {badge}
        </h1>
        {subtitle && <p className="sub">{subtitle}</p>}
        {children}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </header>
  );
}

export interface LifecycleStep {
  label: string;
  state: 'done' | 'current' | 'upcoming' | 'terminal';
}

export function LifecycleRail({ steps }: { steps: LifecycleStep[] }) {
  return (
    <div className="hf-lifecycle" role="list" aria-label="Lifecycle">
      {steps.map((s, i) => (
        <Fragment key={i}>
          {i > 0 && <span className={`bar ${s.state !== 'upcoming' ? 'filled' : ''}`} aria-hidden />}
          <span role="listitem" className={`step ${s.state}`} aria-current={s.state === 'current' ? 'step' : undefined}>
            <span className="dot" aria-hidden />
            {s.label}
          </span>
        </Fragment>
      ))}
    </div>
  );
}

export function Banner({ tone = 'info', children, icon, action }: { tone?: 'info' | 'error' | 'warn' | 'pos'; children: ReactNode; icon?: IconName; action?: ReactNode }) {
  const name: IconName = icon ?? (tone === 'error' ? 'alert-circle' : tone === 'warn' ? 'alert-triangle' : tone === 'pos' ? 'check-circle' : 'info');
  return (
    <div className={`hf-banner ${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon name={name} size={15} style={{ marginTop: 1, flexShrink: 0 }} />
      <div className="hf-grow">{children}</div>
      {action}
    </div>
  );
}

export function ErrorBanner({ error, action }: { error: unknown; action?: ReactNode }) {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <Banner tone="error" action={action}>
      {msg}
    </Banner>
  );
}

export function Loading({ label = 'Loading…', mascot = true }: { label?: string; mascot?: boolean }) {
  // Nova bobs while you wait (stilled under prefers-reduced-motion). `mascot={false}` falls
  // back to the hairline spinner for dense inline spots where a mascot would be too much.
  if (!mascot) {
    return (
      <div className="hf-loading" role="status" aria-live="polite">
        <span className="hf-spinner" /> {label}
      </div>
    );
  }
  return (
    <div className="hf-loading hf-loading-nova" role="status" aria-live="polite">
      <Nova variant="full" mood="happy" size={40} animated />
      <span>{label}</span>
    </div>
  );
}

export function Money({ minor, currency = 'USD', color = false, className = '' }: { minor: string | number | null | undefined; currency?: string; color?: boolean; className?: string }) {
  const sign = color ? moneySign(minor) : '';
  return <span className={`hf-money ${sign} ${className}`}>{formatMoney(minor, currency)}</span>;
}
